// Shared primitives and icons
import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from 'react';
import { useT } from './i18n.jsx';

// ---------------- Icons (stroke-based, 16px) ----------------
const Icon = ({ d, size = 16, fill = 'none', stroke = 'currentColor', strokeWidth = 1.5, children, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={fill} stroke={stroke} strokeWidth={strokeWidth}
       strokeLinecap="round" strokeLinejoin="round" {...rest}>
    {d ? <path d={d} /> : children}
  </svg>
);

const IconSearch = (p) => <Icon {...p}><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 L13.5 13.5" /></Icon>;
const IconArrowRight = (p) => <Icon {...p} d="M3 8h10 M9 4l4 4-4 4" />;
const IconArrowLeft = (p) => <Icon {...p} d="M13 8H3 M7 4L3 8l4 4" />;
const IconArrowUpRight = (p) => <Icon {...p} d="M4 12L12 4 M5 4h7v7" />;
const IconPlus = (p) => <Icon {...p} d="M8 3v10 M3 8h10" />;
const IconCheck = (p) => <Icon {...p} d="M3 8.5l3 3 7-7" />;
const IconX = (p) => <Icon {...p} d="M3.5 3.5l9 9 M12.5 3.5l-9 9" />;
const IconCopy = (p) => <Icon {...p}><rect x="5" y="5" width="8" height="8" rx="1.5" /><path d="M3 10.5V4a1 1 0 0 1 1-1h6.5" /></Icon>;
const IconChevronDown = (p) => <Icon {...p} d="M3.5 6l4.5 4 4.5-4" />;
const IconChevronRight = (p) => <Icon {...p} d="M6 3.5l4 4.5-4 4" />;
const IconPlay = (p) => <Icon {...p}><path d="M5 3.5v9l7-4.5z" fill="currentColor" /></Icon>;
const IconGrid = (p) => <Icon {...p}><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="9" y="2" width="5" height="5" rx="1" /><rect x="2" y="9" width="5" height="5" rx="1" /><rect x="9" y="9" width="5" height="5" rx="1" /></Icon>;
const IconList = (p) => <Icon {...p} d="M3 4h10 M3 8h10 M3 12h10" />;
const IconExternal = (p) => <Icon {...p}><path d="M10 3h3v3 M13 3L7 9 M12 9v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" /></Icon>;
const IconKey = (p) => <Icon {...p}><circle cx="11" cy="6" r="2.5" /><path d="M8.5 7.5L3 13v1h2v-1.5h1.5V11H8" /></Icon>;
const IconBolt = (p) => <Icon {...p}><path d="M9 2L3.5 9h3L7 14l5.5-7h-3z" /></Icon>;
const IconBook = (p) => <Icon {...p}><path d="M3 3h4a2 2 0 0 1 2 2v8a1 1 0 0 0-1-1H3zM13 3H9a2 2 0 0 0-2 2v8a1 1 0 0 1 1-1h5z" /></Icon>;
const IconLayers = (p) => <Icon {...p}><path d="M8 2L2 5l6 3 6-3z M2 9l6 3 6-3 M2 12l6 3 6-3" /></Icon>;
const IconGauge = (p) => <Icon {...p}><circle cx="8" cy="9" r="5.5" /><path d="M8 9l3-3" /></Icon>;
const IconWallet = (p) => <Icon {...p}><rect x="2" y="4" width="12" height="9" rx="1.5" /><path d="M2 7h12 M11 10h1" /></Icon>;
const IconUsers = (p) => <Icon {...p}><circle cx="6" cy="5" r="2.5" /><path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" /><circle cx="11" cy="5" r="2" /><path d="M11 9c1.7 0 3 1.3 3 3" /></Icon>;
const IconShield = (p) => <Icon {...p} d="M8 2L3 4v4c0 3 2 5 5 6 3-1 5-3 5-6V4z" />;
const IconTerminal = (p) => <Icon {...p}><rect x="2" y="3" width="12" height="10" rx="1" /><path d="M5 7l2 2-2 2 M9 11h3" /></Icon>;
const IconSettings = (p) => <Icon {...p}><circle cx="8" cy="8" r="2" /><path d="M8 1v2 M8 13v2 M1 8h2 M13 8h2 M3 3l1.5 1.5 M11.5 11.5 13 13 M3 13l1.5-1.5 M11.5 4.5 13 3" /></Icon>;
const IconBell = (p) => <Icon {...p}><path d="M8 2a4 4 0 0 0-4 4v3l-1 2h10l-1-2V6a4 4 0 0 0-4-4z M6.5 13a1.5 1.5 0 0 0 3 0" /></Icon>;
const IconCpu = (p) => <Icon {...p}><rect x="4" y="4" width="8" height="8" rx="1" /><path d="M6 4V2 M10 4V2 M6 14v-2 M10 14v-2 M4 6H2 M4 10H2 M14 6h-2 M14 10h-2" /></Icon>;
const IconStar = (p) => <Icon {...p}><path d="M8 2l1.8 3.7 4 .6-3 2.9.7 4.1L8 11.4 4.5 13.3l.7-4.1-3-2.9 4-.6z" /></Icon>;
const IconDots = (p) => <Icon {...p}><circle cx="3" cy="8" r="1" fill="currentColor"/><circle cx="8" cy="8" r="1" fill="currentColor"/><circle cx="13" cy="8" r="1" fill="currentColor"/></Icon>;
const IconFilter = (p) => <Icon {...p} d="M2 4h12 M4 8h8 M6 12h4" />;
const IconDownload = (p) => <Icon {...p} d="M8 2v9 M4.5 7.5L8 11l3.5-3.5 M3 13h10" />;
const IconCode = (p) => <Icon {...p} d="M5 4L2 8l3 4 M11 4l3 4-3 4 M9.5 3l-3 10" />;
// X (formerly Twitter) brand mark — official asymmetric "X" glyph. Uses its own
// 24-unit viewBox + currentColor fill (vs. the project-default 16-unit stroke
// icons) because the brand logo is a filled wordmark, not a line drawing.
const IconTwitter = ({ size = 16, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true" {...rest}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const IconGithub = (p) => <Icon {...p}><path d="M8 2a6 6 0 0 0-2 11.7c0-.5 0-1 .1-1.5-2 .4-2.5-1-2.5-1-.3-.7-.8-1-.8-1-.7-.4.1-.4.1-.4.7 0 1.1.8 1.1.8.7 1.1 1.7.8 2.2.6 0-.5.3-.8.5-1-1.6-.2-3.3-.8-3.3-3.6 0-.8.3-1.5.8-2-.1-.2-.3-1 .1-2 0 0 .6-.2 2 .8a7 7 0 0 1 3.6 0c1.4-1 2-.8 2-.8.4 1 .2 1.8.1 2 .5.5.8 1.2.8 2 0 2.8-1.7 3.4-3.3 3.6.3.2.5.7.5 1.4 0 1 0 1.9-.1 2.1A6 6 0 0 0 8 2z" fill="currentColor" stroke="none"/></Icon>;
const IconGlobe = (p) => <Icon {...p}><circle cx="8" cy="8" r="6" /><path d="M2 8h12 M8 2c2 2 2 10 0 12 M8 2c-2 2-2 10 0 12" /></Icon>;
const IconRefresh = (p) => <Icon {...p}><path d="M13 4a6 6 0 1 0 1 5 M13 2v3h-3" /></Icon>;
const IconTrash = (p) => <Icon {...p}><path d="M3 4h10 M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1 M4 4l1 9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-9" /></Icon>;
const IconEdit = (p) => <Icon {...p} d="M11 2l3 3-8 8H3v-3z" />;
const IconEye = (p) => <Icon {...p}><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" /><circle cx="8" cy="8" r="2" /></Icon>;
const IconEyeOff = (p) => <Icon {...p}><path d="M2 2l12 12 M6.5 6.5a2 2 0 0 0 3 3 M4 4.5C2.5 5.5 1 8 1 8s2.5 5 7 5c1.4 0 2.6-.4 3.5-1 M7 3.1c.3 0 .7-.1 1-.1 4.5 0 7 5 7 5s-.5 1-1.5 2.2" /></Icon>;
const IconInfo = (p) => <Icon {...p}><circle cx="8" cy="8" r="6.5"/><path d="M8 7.5v3.5 M8 5.1v.1"/></Icon>;
const IconSun = (p) => <Icon {...p}><circle cx="8" cy="8" r="3" /><path d="M8 1v2 M8 13v2 M1 8h2 M13 8h2 M3 3l1.5 1.5 M11.5 11.5 13 13 M3 13l1.5-1.5 M11.5 4.5 13 3" /></Icon>;
const IconFlame = (p) => <Icon {...p}><path d="M8 14c-3 0-5-2-5-5 0-2 2-3 2-5 1 0 2 1 2 3 1-1 1-3 0-5 2 1 4 3 4 7 0 3-1 5-3 5z" /></Icon>;
const IconBlocks = (p) => <Icon {...p}><rect x="2" y="2" width="5" height="5"/><rect x="9" y="2" width="5" height="5"/><rect x="2" y="9" width="5" height="5"/><path d="M10.5 9.5l3 3 M10.5 13.5l3-3" /></Icon>;
const IconDollar = (p) => <Icon {...p} d="M8 2v12 M11 5H6.5a1.5 1.5 0 0 0 0 3H9.5a1.5 1.5 0 0 1 0 3H5" />;
const IconZap = (p) => <Icon {...p}><path d="M9 1L3 9h3v6l6-8h-3z" /></Icon>;
const IconMessage = (p) => <Icon {...p}><path d="M2 3h12v8H6l-4 3z" /></Icon>;
const IconPause = (p) => <Icon {...p}><rect x="4" y="3" width="3" height="10"/><rect x="9" y="3" width="3" height="10"/></Icon>;
const IconRocket = (p) => <Icon {...p}><path d="M8 2c3 2 4.5 5 4 9l-1 2h-6l-1-2c-.5-4 1-7 4-9z M6 11l-1.5 2.5 2-.5 M10 11l1.5 2.5-2-.5" /><circle cx="8" cy="7" r="1"/></Icon>;
const IconArrowUp = (p) => <Icon {...p} d="M8 13V3 M4 7l4-4 4 4" />;
const IconArrowDown = (p) => <Icon {...p} d="M8 3v10 M4 9l4 4 4-4" />;
const IconMenu = (p) => <Icon {...p} d="M2 4h12 M2 8h12 M2 12h12" />;
const IconCircle = (p) => <Icon {...p}><circle cx="8" cy="8" r="5" /></Icon>;
const IconSliders = (p) => <Icon {...p}><path d="M2 4h8 M13 4h1 M2 8h3 M8 8h6 M2 12h8 M13 12h1"/><circle cx="11.5" cy="4" r="1.5"/><circle cx="6.5" cy="8" r="1.5"/><circle cx="11.5" cy="12" r="1.5"/></Icon>;
const IconMaximize = (p) => <Icon {...p} d="M3 6V3h3 M10 3h3v3 M13 10v3h-3 M6 13H3v-3" />;
// Stylised bar chart — three vertical bars of growing height. Used for the provider Analytics
// nav entry. Plain `d` path keeps the visual weight consistent with the other icons.
const IconBarChart = (p) => <Icon {...p} d="M3 13V9 M7 13V5 M11 13V7 M2 14h12" />;

export const XIcons = {
  IconSearch, IconArrowRight, IconArrowLeft, IconArrowUpRight, IconPlus, IconCheck, IconX, IconCopy,
  IconChevronDown, IconChevronRight, IconPlay, IconGrid, IconList, IconExternal, IconKey, IconBolt,
  IconBook, IconLayers, IconGauge, IconWallet, IconUsers, IconShield, IconTerminal, IconSettings,
  IconBell, IconCpu, IconStar, IconDots, IconFilter, IconDownload, IconCode, IconTwitter, IconGithub,
  IconGlobe, IconRefresh, IconTrash, IconEdit, IconEye, IconEyeOff, IconInfo, IconSun, IconFlame, IconBlocks,
  IconDollar, IconZap, IconRocket, IconArrowUp, IconArrowDown, IconMenu, IconCircle, IconSliders, IconMessage, IconPause,
  IconMaximize, IconBarChart,
};

// ---------------- Method chip ----------------
const MethodChip = ({ method }) => {
  const m = (method || '').toUpperCase();
  const cls = {
    GET: 'method method-get',
    POST: 'method method-post',
    PUT: 'method method-put',
    PATCH: 'method method-patch',
    DELETE: 'method method-delete',
  }[m] || 'method method-get';
  return <span className={cls}>{m}</span>;
};

// ---------------- Logo ----------------
// Source-of-truth asset: /public/fav.png — the actual brand mark used by xapi-new (Layout.tsx:105).
// /public/logo.svg is just a generic placeholder; not used here.
// Pass `markOnly` for places that only want the icon (e.g. favicon-style usage in compact bars).
const Logo = ({ size = 22, markOnly = false }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <img src="/fav.png" width={size} height={size} alt="xAPI" style={{ display: 'block', borderRadius: 4 }} />
    {!markOnly && (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, letterSpacing: '-0.02em', fontWeight: 500 }}>
        xapi<span style={{color:'var(--fg-3)'}}>.to</span>
      </span>
    )}
  </div>
);

// ---------------- Copy to clipboard ----------------
const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = (text) => {
    try { navigator.clipboard.writeText(text); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return [copied, copy];
};

// Two stacked faces (Copy / Copied) cross-faded with a 2px blur — the blur
// bridges the swap so the eye reads one smooth morph instead of two states
// blinking past each other. Grid stacking sizes the button to the wider face,
// so there's no layout jump when the label changes length.
const CopyBtn = ({ text, label }) => {
  const [copied, copy] = useCopy();
  return (
    <button className={`btn btn-sm btn-ghost copy-btn${copied ? ' is-copied' : ''}`} onClick={() => copy(text)}>
      <span className="copy-btn-swap">
        <span className="copy-btn-face"><IconCopy />{label || 'Copy'}</span>
        <span className="copy-btn-face copy-btn-back"><IconCheck />{label || 'Copied'}</span>
      </span>
    </button>
  );
};

// ---------------- JSON viewer with basic syntax highlight ----------------
// Tokenizer is regex-based — fast, small, and good enough for response bodies. Strings get
// HTML-escaped before tokenization so user-provided content can't inject markup.
// Escape only the chars that have meaning inside <pre>: &, <, >. Quotes stay raw — the
// highlight regex below uses raw " as the string delimiter, so we must NOT translate them.
const escapeHtml = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
const highlightJson = (input) => {
  if (!input) return '';
  const escaped = escapeHtml(input);
  // Order matters: match keys ("foo":) and strings BEFORE numbers/literals so values inside
  // strings (e.g. "40%", ISO timestamps) don't get re-tokenized.
  return escaped.replace(
    /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?)/g,
    (m, key, str, lit, num) => {
      if (key) return `<span class="jv-key">${key}</span>`;
      if (str) return `<span class="jv-str">${str}</span>`;
      if (lit === 'null') return `<span class="jv-null">${lit}</span>`;
      if (lit) return `<span class="jv-bool">${lit}</span>`;
      if (num) return `<span class="jv-num">${num}</span>`;
      return m;
    }
  );
};

// Try-format JSON. If `value` is already a string, attempt to parse-then-stringify; otherwise
// stringify directly. On any failure return the raw string so we never lose data.
const formatJson = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const JsonViewer = ({ value, maxHeight = 280, style = {} }) => {
  const text = formatJson(value);
  return (
    <pre
      className="code jv"
      style={{ margin: 0, maxHeight, whiteSpace: 'pre', overflow: 'auto', ...style }}
      dangerouslySetInnerHTML={{ __html: highlightJson(text) }}
    />
  );
};

// Full-screen modal for inspecting long JSON. Esc + backdrop click to close. Header has Copy
// + Close. Body is a JsonViewer at 70vh so it dominates the viewport without forcing scroll.
const JsonModal = ({ open, onClose, title = 'Response body', value }) => {
  const [copied, copy] = useCopy();
  const text = formatJson(value);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '6vh 6vw',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%', height: '100%', maxWidth: 1400, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden',
        }}
      >
        <div className="row between" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
          <div className="row gap-2">
            <span className="micro">{title}</span>
            <span className="micro-mono" style={{ color: 'var(--fg-4)' }}>· {text.length.toLocaleString()} chars</span>
          </div>
          <div className="row gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => copy(text)}>
              {copied ? <IconCheck /> : <IconCopy />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button className="btn btn-sm" onClick={onClose} title="Close (Esc)">Close</button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: 16, overflow: 'auto' }}>
          <pre
            className="code jv"
            style={{ margin: 0, whiteSpace: 'pre', fontSize: 13, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: highlightJson(text) }}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------- Pagination ----------------
// Reusable paging control. Pages are 1-indexed externally for human readability; internal
// math stays unitless. `inline` flag reduces vertical padding for use right under a table.
const Pagination = ({ page, pageSize, total, onPage, inline = false, disabled = false, sequential = false, hasNextPage }) => {
  const { t } = useT();
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize)));
  const safePage = sequential ? Math.max(1, page || 1) : Math.min(Math.max(1, page || 1), totalPages);
  const fromIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const toIdx = Math.min(safePage * pageSize, total || 0);
  const go = (p, intent) => onPage && onPage(sequential ? Math.max(1, p) : Math.min(Math.max(1, p), totalPages), intent);
  return (
    <div className="row gap-2 pagination" style={{
      padding: inline ? '8px 14px' : 0,
      borderTop: inline ? '1px solid var(--line)' : 'none',
      alignItems: 'center',
    }}>
      <span className="micro-mono pagination-range" style={{color:'var(--fg-3)'}}>
        {total > 0 ? `${fromIdx.toLocaleString()}–${toIdx.toLocaleString()} / ${total.toLocaleString()}` : '0'}
      </span>
      <div className="grow pagination-spacer" />
      <button className="btn btn-sm pagination-edge" disabled={disabled || safePage <= 1} onClick={() => go(1, 'first')} title="First">«</button>
      <button className="btn btn-sm pagination-step" disabled={disabled || safePage <= 1} onClick={() => go(safePage - 1, 'previous')} title={t('common.prev')}>
        <IconArrowLeft size={11}/>
        <span className="pagination-step-label">{t('common.prev')}</span>
      </button>
      <span className="micro-mono pagination-page" style={{padding:'0 4px'}}>
        <span className="pagination-page-prefix">{t('common.page')} </span>{safePage} / {totalPages}
      </span>
      <button className="btn btn-sm pagination-step" disabled={disabled || (sequential ? !hasNextPage : safePage >= totalPages)} onClick={() => go(safePage + 1, 'next')} title={t('common.next')}>
        <span className="pagination-step-label">{t('common.next')}</span>
        <IconArrowRight size={11}/>
      </button>
      {!sequential && <button className="btn btn-sm pagination-edge" disabled={disabled || safePage >= totalPages} onClick={() => go(totalPages, 'last')} title="Last">»</button>}
    </div>
  );
};

// ---------------- Placeholder image ----------------
const PlaceholderImg = ({ label, w = '100%', h = 120 }) => (
  <div className="striped hairline" style={{
    width: w, height: h, borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--fg-4)', fontFamily: 'var(--font-mono)', fontSize: 11,
    letterSpacing: '0.05em', textTransform: 'uppercase',
  }}>
    [{label}]
  </div>
);

// ---------------- Sparkline (SVG) ----------------
const Sparkline = ({ values = [], width = 80, height = 20, stroke = 'currentColor', fill = 'none' }) => {
  if (!values.length) return null;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1 || 1);
  const pts = values.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  const area = `0,${height} ${pts} ${width},${height}`;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {fill !== 'none' && <polygon points={area} fill={fill} />}
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.2" />
    </svg>
  );
};

// ---------------- Number formatting ----------------
const fmt = (n) => {
  if (n == null) return '-';
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/,'') + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/,'') + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/,'') + 'k';
  return String(n);
};
const fmtUsd = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------- Tiny-amount (subscript zero notation) ----------------
//
// Per-call API costs can be fractions of a cent (e.g. $0.00000123). Showing them as
// "$0.00" (toFixed(2)) hides the magnitude; showing them as "$0.00000123" is hard to scan.
// CoinGecko / Bloomberg / Binance use a compact subscript notation: write the leading-zero
// run as "0" with a subscript count, then the significant digits.
//
//   1.234       → "$1.23"
//   0.05        → "$0.05"
//   0.0023      → "$0.0023"
//   0.000045    → "$0.0₄45"   ← "0.0" with subscript-4 means "the 0 repeats 4 times" → 4 leading zeros
//   0.00000123  → "$0.0₆123"
//
// Returned as a React fragment so callers can just render it inline:
//   <td>{XPrim.TinyAmount({ value: t.amount, sign: true })}</td>
const TinyAmount = ({ value, prefix = '$', sign = false, dim = 'var(--fg-3)' }) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return React.createElement('span', null, '—');
  if (n === 0) return React.createElement('span', null, prefix + '0');
  const abs = Math.abs(n);
  const signStr = n < 0 ? '-' : (sign ? '+' : '');
  if (abs >= 1) return React.createElement('span', null, signStr + prefix + abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  if (abs >= 0.01) return React.createElement('span', null, signStr + prefix + abs.toFixed(2));
  if (abs >= 0.0001) {
    // 4 sig figs without trailing zeros, but keep at least one digit after decimal.
    let s = abs.toFixed(4).replace(/0+$/, '');
    if (s.endsWith('.')) s += '0';
    return React.createElement('span', null, signStr + prefix + s);
  }
  // < 0.0001 → subscript notation. Per CoinGecko convention: subscript = total leading zeros after ".".
  //   0.000045    → 4 leading zeros → "0.0₄45"
  //   0.00000123  → 5 leading zeros → "0.0₅123"
  // The visible "0.0" reads as "0." followed by a 0 that the subscript multiplies.
  const mag = Math.floor(Math.log10(abs));
  const leadingZeros = -mag - 1;
  // Significant digits: shift to 1.xxx, take 3 sig figs, strip trailing zeros.
  const shifted = abs * Math.pow(10, leadingZeros + 1);
  let sig = shifted.toFixed(2).replace('.', '');
  sig = sig.replace(/0+$/, '') || '0';
  return React.createElement(
    'span', null,
    signStr + prefix + '0.0',
    React.createElement('sub', { style: { fontSize: '0.7em', verticalAlign: 'sub' } }, leadingZeros),
    sig,
  );
};

// String-only variant (for places where React elements aren't possible).
// Uses Unicode subscript digits.
const SUB_DIGITS = ['₀','₁','₂','₃','₄','₅','₆','₇','₈','₉'];
const subscriptNum = (n) => String(n).split('').map(c => SUB_DIGITS[+c] ?? c).join('');
const fmtTinyUsd = (n, sign = false) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (num === 0) return '$0';
  const abs = Math.abs(num);
  const s = num < 0 ? '-' : (sign ? '+' : '');
  if (abs >= 1) return s + '$' + abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (abs >= 0.01) return s + '$' + abs.toFixed(2);
  if (abs >= 0.0001) {
    let str = abs.toFixed(4).replace(/0+$/, '');
    if (str.endsWith('.')) str += '0';
    return s + '$' + str;
  }
  const mag = Math.floor(Math.log10(abs));
  const lz = -mag - 1;
  const shifted = abs * Math.pow(10, lz + 1);
  let sig = shifted.toFixed(2).replace('.', '').replace(/0+$/, '') || '0';
  return s + '$0.0' + subscriptNum(lz) + sig;
};

// ---------------- Category color + label helpers ----------------
// Category palette: each category gets a distinct hue while keeping chroma + lightness
// constant, so chips read as a family rather than a clown college. Values match the
// canonical English category names used in data.jsx and on api records (a.category).
const CATEGORY_HUES = {
  'AI Models':       270,
  'AI-Models':       270,
  'Sandbox':         140,
  'Sandboxes':       140,
  'AI Services':     290,
  'AI-Services':     290,
  'Social':          220,
  'Crypto':          75,
  'Data':            165,
  'Data-Analysis':   165,
  'Search':          320,
  'Media':           30,
  'Dev Tools':       245,
  'Dev-Tools':       245,
  'Public Utils':    195,
  'Public-Utils':    195,
  'Infrastructure':  140,
};

const categoryMeta = (name) => {
  const hue = CATEGORY_HUES[name];
  if (hue == null) {
    return { hue: null, fg: 'var(--fg-3)', bg: 'var(--bg-3)', border: 'var(--line)', dot: 'var(--fg-4)' };
  }
  return {
    hue,
    fg:     `oklch(0.82 0.10 ${hue})`,
    bg:     `color-mix(in oklab, oklch(0.75 0.13 ${hue}) 14%, transparent)`,
    border: `color-mix(in oklab, oklch(0.75 0.13 ${hue}) 38%, var(--line))`,
    dot:    `oklch(0.74 0.16 ${hue})`,
  };
};

// 服务分类规范枚举 —— 必须与后端 shared/constants/service-category.ts 保持一致。
// provider 表单下拉一律从这里渲染，禁止再出现自由文本/带空格的显示名。
// 展示文案走 categoryLabel(t, id) 做本地化，底层值恒为规范英文 ID。
export const SERVICE_CATEGORIES = [
  'Public-Utils',
  'AI-Models',
  'Social',
  'Data-Analysis',
  'Crypto',
];

// English category name → i18n key for the localised label. Returns the English name
// itself when no key is registered (fail-open so unknown categories still render).
const CATEGORY_I18N_KEY = {
  'All':              'category.all',
  'AI Models':        'category.aiModels',
  'AI-Models':        'category.aiModels',
  'Sandbox':          'category.sandbox',
  'Sandboxes':        'category.sandbox',
  'AI Services':      'category.aiServices',
  'AI-Services':      'category.aiServices',
  'Social':           'category.social',
  'Crypto':           'category.crypto',
  'Data':             'category.data',
  'Data-Analysis':    'category.dataAnalysis',
  'Search':           'category.search',
  'Media':            'category.media',
  'Dev Tools':        'category.devTools',
  'Dev-Tools':        'category.devTools',
  'Public Utils':     'category.publicUtils',
  'Public-Utils':     'category.publicUtils',
  'Infrastructure':   'category.infrastructure',
};
const categoryLabel = (t, name) => {
  const key = CATEGORY_I18N_KEY[name];
  if (!key || typeof t !== 'function') return name;
  const v = t(key);
  // If the key wasn't found `t()` returns the literal key — fall back to the English name.
  return v && v !== key ? v : name;
};

// ---------------- Toast — re-exported from src/ui/toast.jsx (Base UI headless) ----------------
// Kept on XPrim namespace so existing call sites (XPrim.useToast(), <XPrim.ToastProvider>) keep working.
import { ToastProvider, useToast } from './ui/toast.jsx';

export const XPrim = {
  MethodChip, Logo, CopyBtn, PlaceholderImg, Sparkline, fmt, fmtUsd,
  TinyAmount, fmtTinyUsd,
  JsonViewer, JsonModal, formatJson,
  Pagination,
  ToastProvider, useToast, useCopy,
  categoryMeta, categoryLabel, SERVICE_CATEGORIES,
};
